'use client';

/**
 * Family / Guardian Access — shared client-side athlete gating hook (Unit U7c).
 *
 * REQUIREMENTS SUMMARY (R22/R23 — early client gate for registration pickers):
 * - This is the SINGLE shared client surface that public registration pickers
 *   (class/team enroll + private-lesson booking) use to decide, per athlete,
 *   whether the signed-in viewer may register that athlete in the ACTIVE family.
 *   Keeping the decision here (not duplicated per page) upholds DRY — same
 *   relationships = same utility = same data everywhere.
 * - It surfaces an earlier, friendlier client gate so a secondary guardian who
 *   lacks "Can register this athlete" sees the athlete DISABLED (not hidden) with
 *   a plain-English reason BEFORE they ever submit and hit the server's 403.
 *   The server gate (resolveEnrollmentActor / assertCanManageFamilyEnrollment)
 *   stays as the security backstop — this only adds a nicer up-front experience.
 *
 * GATING CONTRACT (the rules every caller must follow, enforced here so callers
 * cannot get them wrong):
 *   1. The gate is INERT unless ALL of these hold:
 *        - the feature flag is on (`enabled === true`), AND
 *        - an active family is resolved (`activeFamilyAccountId != null`), AND
 *        - the family context was resolved for the SAME organization the page is
 *          showing (`pageOrganizationId === currentOrganization.id`).
 *      FamilyContext keys off the user's last-selected DASHBOARD org, NOT the
 *      public page's gym. If those differ (or the dashboard org is null for a
 *      not-yet-onboarded parent), we must NOT gate — otherwise we'd scope/disable
 *      against the wrong household. When the orgs don't match we fall back to
 *      exactly today's behavior (every athlete selectable), and the server gate
 *      still protects the action.
 *   2. SCOPE: when the gate is active, athlete options are limited to the active
 *      family's athletes (`activeFamilyAthleteIds`). FAIL-OPEN GUARANTEE (#1867):
 *      that set is server-computed as the active household's athletes UNIONED
 *      with the viewer's verified-guardian athletes (parent_athlete_relationships)
 *      that are org-active but have no active family_athletes row in ANY
 *      household in this org — so scoping can never hide a parent's own child
 *      just because a linking flow skipped the family row (the server also
 *      read-repairs the missing link). Athletes actively linked to a DIFFERENT
 *      household are still scoped out — multi-household separation holds.
 *   3. DISABLE (never hide): when the gate is active AND the viewer cannot manage
 *      enrollments for the active family (`canManageEnrollments === false`), every
 *      shown athlete is disabled with CANNOT_REGISTER_ATHLETE_REASON (the
 *      shared "ask the family's main guardian" sentence).
 *   4. NEVER gate on `canManageEnrollments === false` alone — it defaults to false
 *      while the flag is off (INERT_VALUE), so flag-off / guest / primary-guardian
 *      users would be wrongly blocked. `active` already folds in the flag + family
 *      + org checks, so callers gate on `active`, never on the raw scalar.
 *
 * FLAG SAFETY: while ENABLE_FAMILY_GUARDIAN_ACCESS is off, `enabled` is false, so
 * `active` is false and every helper is a pass-through (no scoping, nothing
 * disabled). Guests and primary guardians are likewise unaffected.
 *
 * DRY: reads the single shared `useFamilyContext()` surface (no per-page recompute
 * of the permission) and the canonical `useOrganization()` for the resolved org.
 */

import { useMemo } from 'react';
import { useFamilyContext } from '@/hooks/family/useFamilyContext';
import { useOrganization } from '@/context/OrganizationContext';
import type { FamilyPermissionSet } from '@/types/family';
import { CANNOT_REGISTER_ATHLETE_MESSAGE } from '@/utils/family/registrationPermissionCopy';

/** Plain-English reason shown on a disabled athlete the viewer can't register. */
export const CANNOT_REGISTER_ATHLETE_REASON = CANNOT_REGISTER_ATHLETE_MESSAGE;

export interface FamilyAthleteGate {
  /**
   * True only when the gate is live for THIS page: flag on, an active family is
   * resolved, AND the family context org matches the page's org. While false the
   * gate is inert and every helper below is a behavior-neutral pass-through.
   */
  active: boolean;
  /**
   * True when the gate is active AND the viewer lacks "Can register this athlete"
   * for the active family. When true, all of the active family's shown athletes
   * are disabled with `CANNOT_REGISTER_ATHLETE_REASON`. Always false while inert.
   */
  blockAllInActiveFamily: boolean;
  /** The active family's athlete ids (empty while inert). */
  activeFamilyAthleteIds: string[];
  /**
   * The household the viewer is acting as on this page, for requests that take a
   * `requestedFamilyAccountId`. Null while inert (flag off, no family, or the family
   * context belongs to a different gym), so the server picks the default household.
   */
  activeFamilyAccountId: string | null;
  /**
   * Limit an athlete-id list to the active family when the gate is active. While
   * inert, returns the input unchanged (today's behavior, all athletes shown).
   */
  scopeAthleteIds: (athleteIds: string[]) => string[];
  /**
   * Limit a list of athlete-shaped objects to the active family when the gate is
   * active. While inert, returns the input unchanged.
   */
  scopeAthletes: <T extends { id: string }>(athletes: T[]) => T[];
  /**
   * True when this specific athlete should be rendered DISABLED because the
   * viewer cannot register them in the active family. Only an active-family
   * athlete can be disabled, and only while the gate is active + the viewer lacks
   * permission. Always false while inert.
   */
  isAthleteDisabled: (athleteId: string) => boolean;
  /**
   * Whether the "Add a new athlete" affordance should be offered on a
   * registration picker. Adding a brand-new athlete to a household is governed by
   * a DIFFERENT permission than registering existing ones: the server
   * (assertCanAddNewAthleteToFamily) allows it only for a primary guardian or a
   * co-guardian who can manage family access. So this is keyed off
   * `can_manage_family_access`, NOT `canManageEnrollments` — a billing/enroll-only
   * secondary may register the family's athletes yet still not add new ones.
   * Pass-through TRUE while the gate is inert (today's behavior). When the gate is
   * active, TRUE only if the viewer can manage the active family's access; callers
   * hide the "Add a new athlete" button when this is false so a secondary isn't
   * walked into a server denial.
   */
  canAddNewAthlete: boolean;
}

/**
 * Resolve the family-aware registration gate for a public registration page.
 *
 * @param pageOrganizationId The org id of the gym/program the page is showing
 *   (the page's OWN resolved org, e.g. programData.organization.id). Pass null
 *   while it is still loading — the gate stays inert until it is known.
 */
export function useFamilyAthleteGate(
  pageOrganizationId: string | null | undefined
): FamilyAthleteGate {
  const {
    enabled,
    activeFamilyAccountId,
    activeFamilyAthleteIds,
    canManageEnrollments,
    effectivePermissions,
  } = useFamilyContext();
  const { currentOrganization } = useOrganization();

  return useMemo<FamilyAthleteGate>(() => {
    // The gate is live ONLY when the flag is on, a family is resolved, and the
    // family context was resolved for the same org this page is showing. The
    // org-match check is the load-bearing correctness guard (see contract rule 1).
    const orgMatches =
      !!pageOrganizationId && currentOrganization?.id === pageOrganizationId;
    const active = enabled && !!activeFamilyAccountId && orgMatches;

    // Disable the active family's athletes only when the viewer lacks the
    // enrollment permission for that family. NEVER keyed off the scalar alone.
    const blockAllInActiveFamily = active && canManageEnrollments === false;

    const familyIds = active ? activeFamilyAthleteIds : [];
    const familyIdSet = new Set(familyIds);

    const scopeAthleteIds = (athleteIds: string[]): string[] =>
      active ? athleteIds.filter((id) => familyIdSet.has(id)) : athleteIds;

    const scopeAthletes = <T extends { id: string }>(athletes: T[]): T[] =>
      active ? athletes.filter((a) => familyIdSet.has(a.id)) : athletes;

    const isAthleteDisabled = (athleteId: string): boolean =>
      blockAllInActiveFamily && familyIdSet.has(athleteId);

    // Adding a NEW athlete is a family-access action, not an enrollment one — so
    // it stays available while inert, and when active it follows
    // can_manage_family_access (a primary's effective set has every flag true, so
    // primaries pass too). Mirrors the server's assertCanAddNewAthleteToFamily.
    const perms = effectivePermissions as FamilyPermissionSet | null;
    const canAddNewAthlete = active
      ? perms?.can_manage_family_access === true
      : true;

    return {
      active,
      blockAllInActiveFamily,
      activeFamilyAthleteIds: familyIds,
      activeFamilyAccountId: active ? activeFamilyAccountId : null,
      scopeAthleteIds,
      scopeAthletes,
      isAthleteDisabled,
      canAddNewAthlete,
    };
  }, [
    enabled,
    activeFamilyAccountId,
    activeFamilyAthleteIds,
    canManageEnrollments,
    effectivePermissions,
    currentOrganization?.id,
    pageOrganizationId,
  ]);
}

export default useFamilyAthleteGate;
